import type { ComputedRef, Ref } from "vue";
import { computed } from "vue";
import { effectiveDifferentialPressureUnit } from "@/libs/units/pressure";
import type { ScenarioCalculations } from "../calculationTypes";
import type {
  OperatingParameters,
  Scenario,
  Sizing,
  WellProperties
} from "@/composables/forms/formTypes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import type { MaybeRef } from "@vueuse/core";
import type { Section, SectionDeps } from "./types";
import { buildPumpSideCalculatorSection } from "./sections/pump";
import { buildMotorSideCalculatorSection } from "./sections/motor";
import { buildHpsMotorSideCalculatorSection } from "./sections/hpsMotor";
import { buildIntakeConditionsSideCalculatorSection } from "./sections/intakeConditions";
import { buildCablesSideCalculatorSection } from "./sections/cables";
import { buildFluidPropertiesSideCalculatorSection } from "./sections/fluidProperties";
import { buildWellTestSideCalculatorSection } from "./sections/wellTest";
import { buildSealSideCalculatorSection } from "./sections/seal";
import { buildThrustChamberSideCalculatorSection } from "./sections/thrustChamber";
import { buildMleSideCalculatorSection } from "./sections/mle";
import { buildSurfaceDriveSideCalculatorSection } from "./sections/surfaceDrive";
import {
  buildOperatingParametersSideCalculatorSection,
  buildWellCompletionSideCalculatorSection
} from "./sections/staticSections";

export type { Section, SectionDeps } from "./types";
export {
  ScenarioDetailSectionKey,
  ScenarioDetailSectionTitle,
  EquipmentWithNoCalculations,
  HIDDEN_WHEN_HPS,
  HIDDEN_WHEN_ESP
} from "./types";

export const useEquipmentCalculatorSections = (
  scenarioCalculations: ScenarioCalculations,
  parameters: Ref<OperatingParameters>,
  wellProperties: Ref<WellProperties>,
  scenario: MaybeRef<Scenario | null | undefined>,
  sizing: MaybeRef<Sizing | null | undefined>
): ComputedRef<Section[]> => {
  const {
    getHydraulicPowerUnit,
    getRealPowerUnit,
    getCurrentUnit,
    getGasFlowUnit,
    getVolumeUnit,
    getPressureUnit
  } = useScenarioUnits(scenario, sizing);

  const differentialPressureTargetUnit = computed(() =>
    effectiveDifferentialPressureUnit(
      getPressureUnit("Differential Pressure.convert.PSIA").value
    )
  );

  const deps: SectionDeps = {
    scenarioCalculations,
    parameters,
    wellProperties,
    differentialPressureTargetUnit,
    getHydraulicPowerUnit,
    getRealPowerUnit,
    getCurrentUnit,
    getGasFlowUnit,
    getVolumeUnit,
    getPressureUnit
  };

  const pumpCalculatorSection = computed<Section>(() =>
    buildPumpSideCalculatorSection(deps)
  );
  const motorCalculatorSection = computed<Section>(() =>
    buildMotorSideCalculatorSection(deps)
  );
  const HpsMotorCalculatorSection = computed<Section>(() =>
    buildHpsMotorSideCalculatorSection(deps)
  );
  const intakeConditionsCalculatorSection = computed<Section>(() =>
    buildIntakeConditionsSideCalculatorSection(deps)
  );
  const cablesCalculatorSection = computed<Section>(() =>
    buildCablesSideCalculatorSection(deps)
  );
  const fluidPropertiesCalculatorSection = computed<Section>(() =>
    buildFluidPropertiesSideCalculatorSection(deps)
  );
  const wellTestCalculatorSection = computed<Section>(() =>
    buildWellTestSideCalculatorSection(deps)
  );
  const sealCalculatorSection = computed<Section>(() =>
    buildSealSideCalculatorSection(deps)
  );
  const thrustChamberCalculatorSection = computed<Section>(() =>
    buildThrustChamberSideCalculatorSection(deps)
  );
  const mleCalculatorSection = computed<Section>(() =>
    buildMleSideCalculatorSection(deps)
  );
  const surfaceDriveCalculatorSection = computed<Section>(() =>
    buildSurfaceDriveSideCalculatorSection(deps)
  );
  const operatingParametersCalculatorSection = computed<Section>(() =>
    buildOperatingParametersSideCalculatorSection()
  );
  const wellCompletionCalculatorSection = computed<Section>(() =>
    buildWellCompletionSideCalculatorSection()
  );

  return computed<Section[]>(() => [
    pumpCalculatorSection.value,
    motorCalculatorSection.value,
    HpsMotorCalculatorSection.value,
    intakeConditionsCalculatorSection.value,
    cablesCalculatorSection.value,
    fluidPropertiesCalculatorSection.value,
    wellTestCalculatorSection.value,
    sealCalculatorSection.value,
    mleCalculatorSection.value,
    surfaceDriveCalculatorSection.value,
    operatingParametersCalculatorSection.value,
    wellCompletionCalculatorSection.value,
    thrustChamberCalculatorSection.value
  ]);
};
